<template>
    <div :id="props.id" class="modal fade" tabindex="-1" role="dialog" aria-labelledby="modalLabelSelectDivision"
        aria-hidden="true" data-bs-backdrop="static" data-bs-keyboard="false">
        <div class="modal-dialog modal-dialog-centered modal-dialog-scv" role="document">
            <div class="modal-content modal-content-scv">
                <div class="modal-header modal-header-scv-cmn" style="width: 100%">
                    <div class="modal-header-text-box-scv" style="width: 100%">
                        <span class="modal-header-text-scv">{{ title }}</span>
                    </div>
                    <div class="btn-scv-close">
                        <a class="btn-scv-close" href="#" role="button" data-bs-dismiss="modal">
                            <div class="icon-font-slategray">
                                <i class="icon-delete"></i>
                            </div>
                        </a>
                    </div>
                </div>
                <div class="modal-body modal-body-scv">
                    <DivisionTreeSelect :divSettings="divSettings" @selectItem="onSelectDivision"
                        :selectDivision="selectDivision" :isInheritViewable="isInheritViewable"
                        :arViewableDivisionID="localViewableDivisionID" :defaultDivisionID="localDefaultDivisionID"
                        style="width: 440px; min-height: 288px;" ref="divSelect" idPrefix="SelectDivisionDialog" />
                    <div v-if="enableInheritDivision" style="padding-top: 16px;">
                        <label class="scv-check-label-box">
                            <input type="checkbox" v-model="localIsInheritDivision" />
                            <span>配下も含める</span>
                        </label>
                    </div>
                </div>
                <div class="modal-footer modal-footer-scv">
                    <div class="d-flex justify-content-center">
                        <div zero-font>
                            <button type="button" class="btn-scv-blue" id="buttonApply" @click="onApplyButton"
                                style="margin-right: 40px;">
                                <slot name="ok_text">適用</slot>
                            </button>
                            <button type="button" class="btn-scv-white" data-bs-dismiss="modal">キャンセル</button>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    </div>
</template>

<script setup lang="ts">
import { ref, onMounted, watch, Ref } from 'vue';
import { cloneDeep } from 'lodash';
import DivisionTreeSelect from '@/components/DivisionTree/DivisionTreeSelect.vue';
import { DivisionData, DivisionDBData, DivisionAgentCountDBData } from "@/models/DivisionData";
import { useUserSettingStore } from '@/models/Store/UserSettingStore';
//import HttpRequestUtil from '@/util/HttpRequestUtil';
import useGetDivisionDataPackage from '@/views/Mobile/Android/GoogleAccountSetting/package/useGetDivisionDataPackage';
import { BootstrapUtil } from "lib-vue-v2/util";

interface Props {
    id: string;
    title: string;
    selectedDivisionID?: number | null;
    alertDivisionDisplay?: boolean;             // 部署ツリーにアラート端末数を表示するかどうか
    enableInheritDivision?: boolean;            // 配下部署を含むかどうかの選択ができるかどうか
    isInheritViewable?: boolean;                // 閲覧設定を子要素に引き継ぐか
    isInheritDivision?: boolean;                // 配下部署を含むかどうか
    viewableDivisionID?: Array<number> | null;  // 閲覧可能部署ID
}
const props = withDefaults(defineProps<Props>(), {
    selectedDivisionID: null,
    alertDivisionDisplay: false,
    enableInheritDivision: false,
    isInheritViewable: true,
    isInheritDivision: true,
    viewableDivisionID: null,
});

interface Emits {
    (event: "onDivisionSelectChange", value: DivisionData, isInheritDivision?: boolean): void;
}
const emit = defineEmits<Emits>();

let isMounted = false;                                  // マウントされたかどうか
const divSettings: Ref<DivisionData> = ref(new DivisionData());
const selectDivision: Ref<DivisionData> = ref(new DivisionData());  // 選択中の部署情報
const localIsInheritDivision = ref(true);                      // 配下部署を含むかどうか(local)
const userSettingStore = useUserSettingStore();
const divSelect: Ref<DivisionTreeSelect | null> = ref(null);
const localViewableDivisionID = ref<Array<number> | null>(null);    // 閲覧可能部署ID
const localDefaultDivisionID = ref<number>(0);                      // 閲覧不可の部署を指定された際に初期選択する部署ID
const getDivisionDataPackage = useGetDivisionDataPackage();

watch(() => props.isInheritDivision, () => {
    localIsInheritDivision.value = props.isInheritDivision;
});
watch(() => props.selectedDivisionID, (newValue, oldValue) => {
    // マウント後の変更で変更がある場合は再度親に通知
    if (isMounted && (oldValue != newValue)) {
        // 選択部署を更新する。
        updateSelectDivision();
        // 親に通知する。
        emit("onDivisionSelectChange", selectDivision.value);
    }
});

/**
* @brief	mounted
* @note    インスタンスが DOM 要素にマウントされた後
* @return	なし
*/
onMounted(async () => {
    // ダイアログを開いた際の処理
    const el = document.getElementById(props.id);
    if (null !== el) {
        el.addEventListener('show.bs.modal', async () => {
            // 選択部署を更新する。
            updateSelectDivision();

            // 配下を含める設定の初期化
            localIsInheritDivision.value = props.isInheritDivision;
        });
        el.addEventListener('shown.bs.modal', () => {
            divSelect.value?.show();
        });
        el.addEventListener('hide.bs.modal', () => {
            divSelect.value?.hide();
        });
    }

    // 部署情報を取得
    await sendReqGetDivision();

    // 選択部署を更新する。
    updateSelectDivision();

    // 閲覧可能部署ID
    if (null != props.viewableDivisionID) {
        localViewableDivisionID.value = cloneDeep(props.viewableDivisionID);
    }
    else {
        localViewableDivisionID.value = userSettingStore.getViewableDivisionIds;
    }

    // 閲覧不可の部署を指定された際に初期選択する部署ID
    if (null != props.viewableDivisionID) {
        if (props.viewableDivisionID.includes(userSettingStore.getDivisionId)) {
            // Piniaの選択部署が閲覧可能であれば、Piniaの選択部署をデフォルトとする
            localDefaultDivisionID.value = userSettingStore.getDivisionId;
        }
        else {
            // Piniaの選択部署が閲覧制限であれば、閲覧可能部署の先頭をデフォルトとする
            localDefaultDivisionID.value = props.viewableDivisionID[0];
        }
    }
    else {
        // Piniaの選択部署をデフォルトとする
        localDefaultDivisionID.value = userSettingStore.getDivisionId;
    }

    // 親に通知する。
    emit("onDivisionSelectChange", selectDivision.value);

    isMounted = true;
});

// 部署情報をDBから取得
async function sendReqGetDivision(): Promise<void> {
    var arDivDBData = new Array<DivisionDBData>();
    /*if (props.alertDivisionDisplay) {
        // アラート表示あり
        const bodyData = {
            OptionAgentCount: false,
            OptionAlertCount: true,
            OptionUIPathName: true
        };

        const res = await HttpRequestUtil.post(
            "/Division/GetDivision",
            bodyData
        );
        if (res.status != 200) {
            return;
        }

        // DBから取得したデータを格納する。
        arDivDBData = res.data.divisionList;

        // アラート端末数
        var arDivAlertDBData = new Array<DivisionAgentCountDBData>();
        arDivAlertDBData = res.data.alertAgentCountList;

        divSettings.value.Load(arDivDBData, arDivAlertDBData);
    }
    else {
        // アラート表示なし
        const bodyData = {
            OptionAgentCount: false,
            OptionAlertCount: false,
            OptionUIPathName: true
        };

        const res = await HttpRequestUtil.post(
            "/Division/GetDivision",
            bodyData
        );
        if (res.status != 200) {
            return;
        }

        // DBから取得したデータを格納する。
        arDivDBData = res.data.divisionList;

        divSettings.value.Load(arDivDBData);
    }*/
    // frontendEx用特殊処理
    const [success, divisions] = await getDivisionDataPackage.executeAsync();
    if (!success) return;
    arDivDBData = divisions;
    divSettings.value.Load(arDivDBData);
}

/**
 * 部署の選択状態を更新する
 */
async function updateSelectDivision(): Promise<void> {
    // 選択状態
    if (null != props.selectedDivisionID) {
        // リンク先のページは部署IDをURLから渡される
        const divisionData = divSettings.value.GetDivisionData(props.selectedDivisionID);
        if (divisionData != undefined) {
            selectDivision.value = divisionData;
        }
        else {
            // 選択部署がなくなっていた
            // 部署ツリーの選択状態を「ネットワーク全体」にする。
            const divisionData = divSettings.value.GetDivisionData(1);
            if (divisionData != undefined) {
                selectDivision.value = divisionData;
            }

            // ネットワーク全体になったことを親に通知する。
            emit("onDivisionSelectChange", selectDivision.value);
        }
    }
    else {
        // 部署ツリーの選択状態を「ネットワーク全体」にする。
        const divisionData = divSettings.value.GetDivisionData(1);
        if (divisionData != undefined) {
            selectDivision.value = divisionData;
        }
    }
}

/**
 * 選択部署の変更通知
 * @param[in] newValue : 選択された部署
 */
function onSelectDivision(newValue: DivisionData): void {
    selectDivision.value = newValue;
}

/**
 * 適用ボタン押下処理
 * @return なし
 */
function onApplyButton(): void {
    // モーダルダイアログを閉じる。
    BootstrapUtil.hideModal(props.id);

    // 親に通知する。
    emit("onDivisionSelectChange", selectDivision.value, localIsInheritDivision.value);
}
</script>

<style scoped></style>